import {I18nIndividual as GedcomIndividual} from "../model/gedcom-individual"
import {I18nFamily as GedcomFamily} from "../model/gedcom-family"
import {v4 as uuid} from "uuid"
import {IntlShape} from "react-intl"
import {NavigateFunction} from "react-router-dom"
import {loadFile} from "../datasource/load-data"
import {validatePipeline} from "./gedcom-validate"
import {ValidationErrors} from "../error/converter-errors"
import {DateOrYear} from "../datepicker/date-or-year"
import md5 from "md5"


/* ------------------------------------------------------------------------------------------------------------------ */
/* Create GEDCOM                                                                                                      */
/* ------------------------------------------------------------------------------------------------------------------ */

export async function creatorToGedcom(individualsData: GedcomIndividual[], familiesData: GedcomFamily[], intl: IntlShape) {
    // Ensure that date columns preserve the prototype and reach validation as plain objects
    const serializedIndividuals = hydrateDateFields(individualsData, ["birthDate", "deceaseDate"])
    const serializedFamilies = hydrateDateFields(familiesData, ["marriageDate"])
    const { validationErrors, validatedIndividuals, validatedFamilies } = validatePipeline(serializedIndividuals, serializedFamilies, intl, true)
    if (validationErrors.length > 0)
        throw new ValidationErrors(
            validationErrors,
            intl.formatMessage({ id: "error.creator.convert" },
            { errorsLength: validationErrors.length })
        )
    return await createGedcomString(validatedIndividuals, validatedFamilies, intl)
}

const hydrateDateFields = <T extends Record<string, any>>(rows: T[], dateFields: (keyof T)[]): T[] => rows.map(row => {
    dateFields.forEach(field => { if (row[field]) (row as any)[field] = new DateOrYear(row[field]) })
    return row
})

async function createGedcomString(individuals: GedcomIndividual[], families: GedcomFamily[], intl: IntlShape) {
    const filename = `${mostFrequentSurname(individuals) ?? intl.formatMessage({ id: "generic.file.name" })}`
    const header = await createHeader(intl, filename)
    const indiRecords = individuals.map(indi => indi.asGedcom(intl.locale)).join("\n")
    const famRecords = families.map(fam => fam.asGedcom()).join("\n")
    const tail = await createTail(filename)
    const gedcomContent = [header, indiRecords, famRecords, tail].join("\n")
    return {gedcomContent, filename}
}

function mostFrequentSurname(individuals: GedcomIndividual[]): string | undefined {
    const counts = individuals.reduce((acc, { surname1 }) => {
        if (surname1) acc[surname1] = (acc[surname1] ?? 0) + 1
        return acc
    }, {} as Record<string, number>)
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0]?.toLowerCase()
}

async function createHeader(intl: IntlShape, filename?: string) {
    const headerFile = await fetch("data/gedcom/header.ged")
    const headerTemplate = await headerFile.text()
    const date = new Date().toLocaleDateString(intl.locale, {day: "2-digit", month: "short", year: "numeric"})
    return headerTemplate
        .replace(/{file}/g, filename ? filename : intl.formatMessage({ id: "generic..file.name" }))
        .replace(/{locale}/g, intl.locale)
        .replace(/{date}/g, date)
        .replace(/{subm}/g, "gedcomcreator.com")
}

async function createTail(filename?: string) {
    let repoSection = ""
    const uid = uuid().toUpperCase()
    if (filename) {
        const repo = `The ${filename
            .replace(/_/g, " ")
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase())} family`
        repoSection += `0 @R0@ REPO\n1 _UID ${uid}\n1 NAME ${repo}\n`
    }
    const tailFile = await fetch("data/gedcom/tail.ged")
    const tailTemplate = await tailFile.text()
    return repoSection + tailTemplate
}

export async function toChart(newFile: File, navigate: NavigateFunction) {
    const { gedcom, images } = await loadFile(newFile)
    const imageFileNames = Array.from(images.keys()).sort().join("|")
    const hash = md5(md5(gedcom) + imageFileNames)
    navigate("/view", { replace: false, state: { data: gedcom, images, hash } })
}